import type {
  RobotColor, TargetColor, Direction, Position, Robot, Target, WallSet,
} from '@ricochet/game-engine';
import { hasWall } from '@ricochet/game-engine';

// ─────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────

export const CELL = 42;
export const BORDER = 3;
export const BASE_BOARD_PX = 16 * CELL;

export const ROBOT_META: Record<RobotColor, {
  fill: string; stroke: string; glow: string; label: string; cssVar: string; iconVar: string;
}> = {
  red:    { fill: 'var(--robot-r)', stroke: 'var(--ink)',    glow: 'var(--robot-r)', label: 'R', cssVar: '--robot-r', iconVar: '--robot-r-icon' },
  blue:   { fill: 'var(--robot-b)', stroke: 'var(--ink)',    glow: 'var(--robot-b)', label: 'B', cssVar: '--robot-b', iconVar: '--robot-b-icon' },
  green:  { fill: 'var(--robot-g)', stroke: 'var(--ink)',    glow: 'var(--robot-g)', label: 'G', cssVar: '--robot-g', iconVar: '--robot-g-icon' },
  yellow: { fill: 'var(--robot-y)', stroke: 'var(--ink)',    glow: 'var(--robot-y)', label: 'Y', cssVar: '--robot-y', iconVar: '--robot-y-icon' },
  silver: { fill: 'var(--robot-silver, #A8A0BC)', stroke: 'var(--ink)', glow: 'var(--robot-silver, #A8A0BC)', label: 'S', cssVar: '--robot-silver', iconVar: '--robot-silver-icon' },
};

export type IconKey = 'bolt' | 'eclipse' | 'disc2' | 'currency' | 'loader-pinwheel';

export const COLOR_ICON: Record<RobotColor, IconKey> = {
  red: 'bolt',
  blue: 'eclipse',
  green: 'disc2',
  yellow: 'currency',
  silver: 'disc2',
};

const ANY_QUAD_COLOR: Record<string, RobotColor> = { n: 'yellow', e: 'red', s: 'green', w: 'blue' };

export function IconShape({ icon, strokeWidth = 2 }: { icon: IconKey; strokeWidth?: number }) {
  const sw = strokeWidth;
  switch (icon) {
    case 'bolt':
      return (
        <>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={12} cy={12} r={4}
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case 'eclipse':
      return (
        <>
          <circle cx={12} cy={12} r={10}
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 2a7 7 0 1 0 10 10"
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case 'disc2':
      return (
        <>
          <circle cx={12} cy={12} r={10}
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={12} cy={12} r={4}
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case 'currency':
      return (
        <>
          <circle cx={12} cy={12} r={8}
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <line x1={3} x2={6} y1={3} y2={6} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <line x1={21} x2={18} y1={3} y2={6} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <line x1={3} x2={6} y1={21} y2={18} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <line x1={21} x2={18} y1={21} y2={18} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case 'loader-pinwheel':
      return (
        <>
          <path d="M22 12a1 1 0 0 1-10 0 1 1 0 0 0-10 0"
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 20.7a1 1 0 1 1 5-8.7 1 1 0 1 0 5-8.6"
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 3.3a1 1 0 1 1 5 8.6 1 1 0 1 0 5 8.6"
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={12} cy={12} r={10}
            fill="none" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
  }
}

/** A single robot as a self-contained square SVG: a colour-filled disc with its
 *  board icon centred (icon spans ~65% of the disc, matching `RobotsLayer`'s
 *  `iconSize = R * 1.3`). Unlike `RobotsLayer` this needs no board geometry, grid
 *  position, or hit targets — so it's reusable anywhere a robot avatar is wanted
 *  (card decorations, badges, etc.). `size` is any CSS length; defaults to 100%. */
export function RobotGlyph({ color, size = '100%' }: { color: RobotColor; size?: number | string }) {
  const meta = ROBOT_META[color];
  const sc = (24 * 0.65) / 24; // icon = 65% of the 24-unit disc
  const off = (24 - 24 * 0.65) / 2;
  return (
    <svg viewBox="0 0 24 24" style={{ width: size, height: size, display: 'block' }} xmlns="http://www.w3.org/2000/svg">
      <circle cx={12} cy={12} r={11.5} fill={meta.fill} />
      <g transform={`translate(${off} ${off}) scale(${sc})`}
        stroke={`var(${meta.iconVar}, rgba(0,0,0,0.35))`} fill="none">
        <IconShape icon={COLOR_ICON[color]} strokeWidth={2.8} />
      </g>
    </svg>
  );
}

const ANY_TARGET_META = { fill: 'var(--ink-soft)' };

export function targetFill(color: TargetColor): string {
  return color === 'any' ? ANY_TARGET_META.fill : ROBOT_META[color].fill;
}

// ─────────────────────────────────────────────
//  Board geometry
// ─────────────────────────────────────────────

export interface BoardGeometry {
  gridSize: number;
  cellSize: number;
  border: number;
  boardPx: number;
  totalSize: number;
  wallThick: number;
  wallOverhang: number;
  cx: (x: number) => number;
  cy: (y: number) => number;
  bx: (x: number) => number;
  by: (y: number) => number;
}

export function createBoardGeometry(opts: {
  gridSize: number;
  cellSize: number;
  border: number;
  minWallThick?: number;
  wallOverhang?: number;
}): BoardGeometry {
  const { gridSize, cellSize, border } = opts;
  const boardPx = gridSize * cellSize;
  const wallThick = Math.max(opts.minWallThick ?? 3, cellSize * 0.11);
  const wallOverhang = opts.wallOverhang ?? 2;

  return {
    gridSize, cellSize, border, boardPx,
    totalSize: boardPx + border * 2,
    wallThick, wallOverhang,
    cx: (x: number) => border + x * cellSize + cellSize / 2,
    cy: (y: number) => border + y * cellSize + cellSize / 2,
    bx: (x: number) => border + x * cellSize,
    by: (y: number) => border + y * cellSize,
  };
}

// ─────────────────────────────────────────────
//  Wall computation (pure functions)
// ─────────────────────────────────────────────

export interface WallRect {
  x: number; y: number; w: number; h: number; key: string;
}

export function computeWallRects(walls: WallSet, geo: BoardGeometry): WallRect[] {
  const { gridSize, cellSize, wallThick, wallOverhang, bx, by } = geo;
  const rects: WallRect[] = [];
  const seen = new Set<string>();

  for (let wy = 0; wy < gridSize; wy++) {
    for (let wx = 0; wx < gridSize; wx++) {
      if (hasWall(walls, wx, wy, 'N') && wy > 0) {
        const k = `h-${wx}-${wy}`;
        if (!seen.has(k)) {
          seen.add(k);
          rects.push({ x: bx(wx) - wallOverhang, y: by(wy) - wallThick / 2, w: cellSize + wallOverhang * 2, h: wallThick, key: k });
        }
      }
      if (hasWall(walls, wx, wy, 'S') && wy < gridSize - 1) {
        const k = `h-${wx}-${wy + 1}`;
        if (!seen.has(k)) {
          seen.add(k);
          rects.push({ x: bx(wx) - wallOverhang, y: by(wy + 1) - wallThick / 2, w: cellSize + wallOverhang * 2, h: wallThick, key: k });
        }
      }
      if (hasWall(walls, wx, wy, 'W') && wx > 0) {
        const k = `v-${wx}-${wy}`;
        if (!seen.has(k)) {
          seen.add(k);
          rects.push({ x: bx(wx) - wallThick / 2, y: by(wy) - wallOverhang, w: wallThick, h: cellSize + wallOverhang * 2, key: k });
        }
      }
      if (hasWall(walls, wx, wy, 'E') && wx < gridSize - 1) {
        const k = `v-${wx + 1}-${wy}`;
        if (!seen.has(k)) {
          seen.add(k);
          rects.push({ x: bx(wx + 1) - wallThick / 2, y: by(wy) - wallOverhang, w: wallThick, h: cellSize + wallOverhang * 2, key: k });
        }
      }
    }
  }
  return rects;
}

export function computeFrameBars(geo: BoardGeometry): { x: number; y: number; w: number; h: number }[] {
  const { border, boardPx, wallThick, wallOverhang } = geo;
  return [
    { x: border - wallOverhang, y: border - wallThick / 2, w: boardPx + wallOverhang * 2, h: wallThick },
    { x: border - wallOverhang, y: border + boardPx - wallThick / 2, w: boardPx + wallOverhang * 2, h: wallThick },
    { x: border - wallThick / 2, y: border - wallOverhang, w: wallThick, h: boardPx + wallOverhang * 2 },
    { x: border + boardPx - wallThick / 2, y: border - wallOverhang, w: wallThick, h: boardPx + wallOverhang * 2 },
  ];
}

export function computeMajorLines(geo: BoardGeometry): { x1: number; y1: number; x2: number; y2: number; key: string }[] {
  const { gridSize, cellSize, border, boardPx } = geo;
  const lines: { x1: number; y1: number; x2: number; y2: number; key: string }[] = [];
  for (let i = 4; i < gridSize; i += 4) {
    lines.push({ x1: border, y1: border + i * cellSize, x2: border + boardPx, y2: border + i * cellSize, key: `mh-${i}` });
    lines.push({ x1: border + i * cellSize, y1: border, x2: border + i * cellSize, y2: border + boardPx, key: `mv-${i}` });
  }
  return lines;
}

// ─────────────────────────────────────────────
//  SVG primitive components
// ─────────────────────────────────────────────

export function BoardDefs({
  geo, idPrefix,
  gridStrokeWidth = 1, gridOpacity = 0.6,
  includeShadowFilter = true,
  children,
}: {
  geo: BoardGeometry;
  idPrefix: string;
  gridStrokeWidth?: number;
  gridOpacity?: number;
  includeShadowFilter?: boolean;
  children?: React.ReactNode;
}) {
  const { cellSize, border } = geo;
  return (
    <defs>
      <pattern id={`${idPrefix}-grid`} width={cellSize} height={cellSize}
        patternUnits="userSpaceOnUse" x={border} y={border}>
        <path d={`M ${cellSize} 0 L 0 0 0 ${cellSize}`} fill="none"
          stroke="var(--line-strong)" strokeWidth={gridStrokeWidth} opacity={gridOpacity} />
      </pattern>
      {includeShadowFilter && (
        <filter id={`${idPrefix}-wallShadow`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1.2" stdDeviation="0.7" style={{ floodColor: 'var(--shadow-wall-flood)' }} />
        </filter>
      )}
      {children}
    </defs>
  );
}

export function BoardBackground({ geo, outerRx = 14 }: { geo: BoardGeometry; outerRx?: number }) {
  const { totalSize, border, boardPx } = geo;
  return (
    <>
      <rect width={totalSize} height={totalSize} fill="var(--surface)" rx={outerRx} />
      <rect x={border} y={border} width={boardPx} height={boardPx} fill="var(--surface)" rx={4} />
    </>
  );
}

export function GridLayer({
  geo, patternId, showMajorLines = true, majorLines,
}: {
  geo: BoardGeometry;
  patternId: string;
  showMajorLines?: boolean;
  majorLines?: ReturnType<typeof computeMajorLines>;
}) {
  const { border, boardPx } = geo;
  return (
    <>
      <rect x={border} y={border} width={boardPx} height={boardPx} fill={`url(#${patternId})`} />
      {showMajorLines && majorLines?.map(l => (
        <line key={l.key} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke="var(--line-strong)" strokeWidth={1} opacity={0.55} />
      ))}
    </>
  );
}

const ICON_CENTER = 12;

export function TargetsLayer({
  geo, targets, idPrefix,
  inset = 1, strokeWidth: strokeWidthProp, strokeDasharray = '',
  strokeOpacity = 0.65, cornerRadius = 0.16, fillOpacity = 0.18, starYOffset = -2, showStar = true,
  fadePosition, fadeColor,
}: {
  geo: BoardGeometry;
  targets: Target[];
  idPrefix: string;
  inset?: number;
  strokeWidth?: number;
  strokeDasharray?: string;
  strokeOpacity?: number;
  cornerRadius?: number;
  fillOpacity?: number;
  starYOffset?: number;
  showStar?: boolean;
  fadePosition?: Position | null;
  fadeColor?: TargetColor | null;
}) {
  const { cellSize, cx, cy, bx, by } = geo;
  const strokeWidth = strokeWidthProp ?? cellSize * 0.083;
  return (
    <>
      {targets.map((t, i) => {
        const isAny = t.color === 'any';
        const fill = isAny ? 'none' : targetFill(t.color);
        const faded = fadePosition != null && fadeColor != null
          && fadePosition.x === t.position.x && fadePosition.y === t.position.y
          && fadeColor === t.color;
        const tx = bx(t.position.x) + inset;
        const ty = by(t.position.y) + inset;
        const sz = cellSize - inset * 2;
        const rx = cellSize * cornerRadius;

        if (isAny) {
          const ext = strokeWidth + 1;
          const ccx = tx + sz / 2;
          const ccy = ty + sz / 2;
          // Diamond (45°-rotated) divisions: N=yellow, E=red, S=green, W=blue
          const triQuads = [
            {
              q: 'n', color: 'var(--robot-y)',
              fill: `${tx},${ty} ${tx+sz},${ty} ${ccx},${ccy}`,
              clip: `${tx-ext},${ty-ext} ${tx+sz+ext},${ty-ext} ${ccx},${ccy}`,
            },
            {
              q: 'e', color: 'var(--robot-r)',
              fill: `${tx+sz},${ty} ${tx+sz},${ty+sz} ${ccx},${ccy}`,
              clip: `${tx+sz+ext},${ty-ext} ${tx+sz+ext},${ty+sz+ext} ${ccx},${ccy}`,
            },
            {
              q: 's', color: 'var(--robot-g)',
              fill: `${tx+sz},${ty+sz} ${tx},${ty+sz} ${ccx},${ccy}`,
              clip: `${tx+sz+ext},${ty+sz+ext} ${tx-ext},${ty+sz+ext} ${ccx},${ccy}`,
            },
            {
              q: 'w', color: 'var(--robot-b)',
              fill: `${tx},${ty+sz} ${tx},${ty} ${ccx},${ccy}`,
              clip: `${tx-ext},${ty+sz+ext} ${tx-ext},${ty-ext} ${ccx},${ccy}`,
            },
          ];
          return (
            <g key={i} opacity={faded ? 0.2 : 1} style={faded ? { pointerEvents: 'none' } : undefined}>
              <clipPath id={`${idPrefix}-qa-${i}`}>
                <rect x={tx} y={ty} width={sz} height={sz} rx={rx} />
              </clipPath>
              {triQuads.map(({ q, clip }) => (
                <clipPath key={q} id={`${idPrefix}-q${q}-${i}`}>
                  <polygon points={clip} />
                </clipPath>
              ))}
              <g clipPath={`url(#${idPrefix}-qa-${i})`}>
                {triQuads.map(({ q, color, fill: pts }) => (
                  <polygon key={q} points={pts} fill={color} fillOpacity={0.35} />
                ))}
              </g>
              {triQuads.map(({ q, color }) => (
                <rect key={q} clipPath={`url(#${idPrefix}-q${q}-${i})`}
                  x={tx} y={ty} width={sz} height={sz} fill="none"
                  stroke={color} strokeWidth={strokeWidth} strokeOpacity={0.65} rx={rx} />
              ))}
              {showStar && (() => {
                const scx = cx(t.position.x);
                const scy = cy(t.position.y);
                const ss = Math.round(cellSize * 0.55);
                const sc = ss / 24;
                return triQuads.map(({ q, color }) => (
                  <g key={q} clipPath={`url(#${idPrefix}-q${q}-${i})`}
                    style={{ pointerEvents: 'none' }}>
                    <g transform={`translate(${scx - ICON_CENTER * sc},${scy - ICON_CENTER * sc}) scale(${sc})`}
                      stroke={color} strokeOpacity={0.85}>
                      <IconShape icon="loader-pinwheel" strokeWidth={2.5} />
                    </g>
                  </g>
                ));
              })()}
            </g>
          );
        }

        return (
          <g key={i} opacity={faded ? 0.2 : 1} style={faded ? { pointerEvents: 'none' } : undefined}>
            <rect
              x={tx} y={ty} width={sz} height={sz}
              fill={fill} fillOpacity={fillOpacity}
              stroke={fill} strokeWidth={strokeWidth}
              strokeOpacity={strokeOpacity}
              strokeDasharray={strokeDasharray || undefined}
              rx={rx}
            />
            {showStar && (() => {
              const scx = cx(t.position.x);
              const scy = cy(t.position.y);
              const ss = Math.round(cellSize * 0.55);
              const sc = ss / 24;
              return (
                <g transform={`translate(${scx - ICON_CENTER * sc},${scy - ICON_CENTER * sc}) scale(${sc})`}
                  stroke={fill} style={{ pointerEvents: 'none' }}>
                  <IconShape icon={COLOR_ICON[t.color as RobotColor]} strokeWidth={2.5} />
                </g>
              );
            })()}
          </g>
        );
      })}
    </>
  );
}

export function WallsLayer({
  wallRects, frameBars, wallThick, shadowFilterId,
}: {
  wallRects: WallRect[];
  frameBars: { x: number; y: number; w: number; h: number }[];
  wallThick: number;
  shadowFilterId?: string;
}) {
  const filter = shadowFilterId ? `url(#${shadowFilterId})` : undefined;
  return (
    <>
      {wallRects.map(r => (
        <rect key={r.key}
          x={r.x} y={r.y} width={r.w} height={r.h}
          rx={wallThick / 2} ry={wallThick / 2}
          fill="var(--ink)" filter={filter}
        />
      ))}
      {frameBars.map((b, i) => (
        <rect key={`frame-${i}`}
          x={b.x} y={b.y} width={b.w} height={b.h}
          rx={wallThick / 2} ry={wallThick / 2}
          fill="var(--ink)" filter={filter}
        />
      ))}
    </>
  );
}

export function RobotsLayer({
  geo, robots,
  radiusFraction = 0.40, showLabels = true,
  animated = false, resetting = false,
  selectedRobot, fadeTest, onRobotClick,
}: {
  geo: BoardGeometry;
  robots: Robot[];
  radiusFraction?: number;
  showLabels?: boolean;
  animated?: boolean;
  resetting?: boolean;
  selectedRobot?: RobotColor | null;
  fadeTest?: (robot: Robot) => boolean;
  onRobotClick?: (id: RobotColor) => void;
}) {
  const { cellSize, cx, cy } = geo;
  const R = cellSize * radiusFraction;

  return (
    <>
      {robots.map(robot => {
        const meta = ROBOT_META[robot.id];
        const isSelected = selectedRobot === robot.id;
        const faded = fadeTest?.(robot) ?? false;
        const tx = cx(robot.position.x);
        const ty = cy(robot.position.y);

        return (
          <g
            key={robot.id}
            onClick={onRobotClick ? () => onRobotClick(robot.id) : undefined}
            opacity={faded ? 0.2 : 1}
            style={animated
              ? {
                  cursor: onRobotClick ? 'pointer' : undefined,
                  transform: `translate(${tx}px, ${ty}px)`,
                  transition: `transform ${resetting ? '0.05s' : '0.18s'} ease-in-out`,
                }
              : { pointerEvents: faded ? 'none' : undefined }
            }
            transform={animated ? undefined : `translate(${tx}, ${ty})`}
          >
            {isSelected && (
              <circle
                r={R + 1}
                fill="none"
                stroke={meta.fill}
                strokeWidth={1}
                strokeDasharray="4 3"
                style={{ animation: 'spin-ring 8s linear infinite' }}
              />
            )}
            {/* Invisible expanded hit target for touch */}
            {onRobotClick && (
              <circle r={cellSize * 0.55} fill="transparent" />
            )}
            <circle r={R} fill={meta.fill} stroke="none" pointerEvents="none" />
            {showLabels && (() => {
              const iconSize = R * 1.3;
              const sc = iconSize / 24;
              return (
                <g transform={`translate(${-ICON_CENTER * sc},${-ICON_CENTER * sc}) scale(${sc})`}
                  stroke={`var(${meta.iconVar}, rgba(0,0,0,0.35))`}
                  style={{ pointerEvents: 'none' }}>
                  <IconShape icon={COLOR_ICON[robot.id]} strokeWidth={2.8} />
                </g>
              );
            })()}
          </g>
        );
      })}
    </>
  );
}
